import { createTheme } from "@mui/material/styles";
import { Box, ButtonBase, IconButton } from "@mui/material";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import FirstPageRoundedIcon from "@mui/icons-material/FirstPageRounded";
import LastPageRoundedIcon from "@mui/icons-material/LastPageRounded";

function NumberedTablePaginationActions({ count, page, rowsPerPage, onPageChange, className }) {
    const totalPages = count < 0 ? null : rowsPerPage > 0 ? Math.ceil(count / rowsPerPage) : 1;
    const isFirstPage = page === 0;
    const isLastPage = totalPages !== null && (totalPages === 0 || page >= totalPages - 1);

    const visiblePages = () => {
        if (totalPages === null || totalPages <= 7) {
            return Array.from({ length: totalPages || 1 }, (_, index) => index + 1);
        }

        const currentPage = page + 1;
        const pages = new Set([1, totalPages]);

        for (let p = Math.max(2, currentPage - 1); p <= Math.min(totalPages - 1, currentPage + 1); p += 1) {
            pages.add(p);
        }

        const orderedPages = [...pages].sort((a, b) => a - b);
        const items = [];
        let last = 0;

        orderedPages.forEach((p) => {
            if (last && p > last + 1) {
                items.push("ellipsis");
            }
            items.push(p);
            last = p;
        });

        return items;
    };

    const items = visiblePages();

    return (
        <Box className={className} sx={{ display: "flex", alignItems: "center", flexShrink: 0, gap: 0.5 }}>
            <IconButton
                aria-label="Go to first page"
                disabled={isFirstPage}
                onClick={(event) => onPageChange(event, 0)}
                size="small"
            >
                <FirstPageRoundedIcon fontSize="small" />
            </IconButton>
            <IconButton
                aria-label="Go to previous page"
                disabled={isFirstPage}
                onClick={(event) => onPageChange(event, page - 1)}
                size="small"
            >
                <ChevronLeftRoundedIcon fontSize="small" />
            </IconButton>
            {items.map((item, index) => {
                if (item === "ellipsis") {
                    return (
                        <Box key={`ellipsis-${index}`} sx={{ px: 0.75, color: "text.secondary", fontSize: "0.8rem" }}>
                            ...
                        </Box>
                    );
                }

                const isSelected = item - 1 === page;
                return (
                    <ButtonBase
                        key={item}
                        component="button"
                        type="button"
                        aria-label={`Page ${item}`}
                        aria-current={isSelected ? "page" : undefined}
                        className={`table-pagination-page${isSelected ? " table-pagination-page-selected" : ""}`}
                        onClick={(event) => onPageChange(event, item - 1)}
                        sx={{
                            minWidth: 28,
                            height: 28,
                            px: 1,
                            borderRadius: 1,
                            background: isSelected ? "rgba(59,130,246,0.13)" : "transparent",
                            color: isSelected ? "primary.main" : "text.primary",
                            fontSize: "0.8rem",
                            fontWeight: isSelected ? 700 : 500,
                        }}
                    >
                        {item}
                    </ButtonBase>
                );
            })}
            <IconButton
                aria-label="Go to next page"
                disabled={isLastPage}
                onClick={(event) => onPageChange(event, page + 1)}
                size="small"
            >
                <ChevronRightRoundedIcon fontSize="small" />
            </IconButton>
            <IconButton
                aria-label="Go to last page"
                disabled={isLastPage}
                onClick={(event) => onPageChange(event, totalPages - 1)}
                size="small"
            >
                <LastPageRoundedIcon fontSize="small" />
            </IconButton>
        </Box>
    );
}

const glassTheme = createTheme({
    palette: {
        mode: "dark",
        primary: {
            main: "#7c3aed",
        },
        background: {
            default: "transparent",
            paper: "rgba(255,255,255,0.06)",
        },
        text: {
            primary: "#000000ff",
            secondary: "rgba(0, 0, 0, 0.7)",
        },
    },

    shape: {
        borderRadius: 18,
    },

    typography: {
        fontFamily: "Inter, sans-serif",
    },

    components: {
        MuiTablePagination: {
            defaultProps: {
                ActionsComponent: NumberedTablePaginationActions,
            },
        },

        MuiPagination: {
            defaultProps: {
                showFirstButton: true,
                showLastButton: true,
                boundaryCount: 1,
                siblingCount: 1,
            },
        },

        // 🧊 GLOBAL CARD
        MuiCard: {
            styleOverrides: {
                root: {
                    background: "rgba(255,255,255,0.06)",
                    backdropFilter: "blur(25px)",
                    WebkitBackdropFilter: "blur(25px)",
                    border: "1px solid rgba(255,255,255,0.12)",
                    boxShadow: `
            0 8px 32px rgba(0,0,0,0.25),
            inset 0 1px 0 rgba(255,255,255,0.2)
          `,
                },
            },
        },

        // 🧊 PAPER
        MuiPaper: {
            styleOverrides: {
                root: {
                    background: "rgba(255,255,255,0.05)",
                    backdropFilter: "blur(20px)",
                    border: "1px solid rgba(255,255,255,0.1)",
                },
            },
        },

        // 🧊 BUTTON
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 14,
                    textTransform: "none",
                    fontWeight: 600,
                },
                contained: {
                    background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                    boxShadow: "0 10px 25px rgba(99,102,241,0.4)",
                },
            },
        },

        // 🧊 INPUT
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    background: "rgba(255,255,255,0.06)",
                    backdropFilter: "blur(20px)",
                    borderRadius: 14,
                    "& fieldset": {
                        borderColor: "rgba(255,255,255,0.2)",
                    },
                    "&:hover fieldset": {
                        borderColor: "rgba(255,255,255,0.4)",
                    },
                },
            },
        },

        // 🧊 LIST ITEM (Sidebar)
        MuiListItemButton: {
            styleOverrides: {
                root: {
                    borderRadius: 14,
                    marginBottom: 8,
                    transition: "all 0.3s ease",
                    "&:hover": {
                        background: "rgba(255,255,255,0.08)",
                    },
                    "&.Mui-selected": {
                        background:
                            "linear-gradient(135deg, rgba(99,102,241,0.6), rgba(139,92,246,0.5))",
                        boxShadow: "0 0 20px rgba(99,102,241,0.5)",
                    },
                },
            },
        },

        // 🧊 APP BAR / HEADER
        MuiAppBar: {
            styleOverrides: {
                root: {
                    background: "rgba(255,255,255,0.06)",
                    backdropFilter: "blur(20px)",
                    borderBottom: "1px solid rgba(255,255,255,0.1)",
                },
            },
        },
        // 🗓️ DATE PICKER CALENDAR ICON BUTTON
        MuiIconButton: {
            styleOverrides: {
                root: {
                    // Target calendar icon buttons inside DatePicker input adornments
                    ".MuiInputAdornment-root &": {
                        color: "#000000",
                        border: "1px solid rgba(0, 0, 0, 0)",
                        borderRadius: "6px",
                        padding: "3px",
                        "&:hover": {
                            backgroundColor: "rgba(0,0,0,0.06)",

                        },
                    },
                },
            },
        },
    },
});

export default glassTheme;